import { useState } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography,
  List, ListItem, Button, Divider, Tooltip,
} from '@mui/material';
import NotificationsIcon     from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { colors } from '../colors';

function relativeTime(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000)       return "à l'instant";
  if (diff < 3_600_000)    return `il y a ${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000)   return `il y a ${Math.floor(diff / 3_600_000)} h`;
  return `il y a ${Math.floor(diff / 86_400_000)} j`;
}

export default function NotificationBell() {
  // Defined inside the component so it re-evaluates on each render,
  // picking up the current palette after a theme change.
  const TYPE_COLOR = {
    info:    colors.info,
    success: colors.success,
    warning: colors.warning,
    error:   colors.error,
  };
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [anchor, setAnchor] = useState(null);
  const navigate = useNavigate();

  const handleOpen  = (e) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);

  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.action_url) { navigate(n.action_url); handleClose(); }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={handleOpen} aria-label="Notifications" sx={{ color: colors.textSecondary }}>
          <Badge badgeContent={unreadCount || null} color="error" max={9}>
            {unreadCount > 0
              ? <NotificationsIcon />
              : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              width: 360, maxHeight: 480,
              bgcolor: colors.bgPaper,
              backgroundImage: 'none', // neutralize MUI dark-mode elevation overlay
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700} color="text.primary">Notifications</Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={markAllRead}
              sx={{ color: colors.primaryLight, fontSize: '0.75rem', minWidth: 0 }}
            >
              Tout marquer lu
            </Button>
          )}
        </Box>
        <Divider sx={{ borderColor: colors.border }} />

        {/* Liste */}
        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ color: colors.textMuted, fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">Aucune notification</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', maxHeight: 3 * 72 }}>
            {notifications.map((n, i) => (
              <Box key={n.id}>
                <ListItem
                  onClick={() => handleClick(n)}
                  sx={{
                    cursor: n.action_url ? 'pointer' : 'default',
                    bgcolor: n.read ? 'transparent' : colors.bgSubtle,
                    '&:hover': { bgcolor: colors.bgMuted },
                    alignItems: 'flex-start',
                    gap: 1.5,
                    px: 2, py: 1.5,
                  }}
                >
                  {/* Colored unread indicator */}
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0, mt: 0.75,
                    bgcolor: n.read ? 'transparent' : (TYPE_COLOR[n.type] || colors.primary),
                  }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={n.read ? 400 : 600}
                      color="text.primary"
                      sx={{ lineHeight: 1.4 }}
                    >
                      {n.title}
                    </Typography>
                    {n.body && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {n.body}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 0.5 }}>
                      {relativeTime(n.created_at)}
                    </Typography>
                  </Box>
                </ListItem>
                {i < notifications.length - 1 && <Divider sx={{ borderColor: colors.border }} />}
              </Box>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
